/**
 * MapListRow — one row in an editor's "Open / Load map" dialog. Shared by all
 * three editors (Synth VR, Flow, Console) so every library list looks identical.
 *
 * Two flavours, driven by which props are passed:
 *   • Rich  — server maps with metadata: artist + "mapped by …", plus right-side
 *             pills (difficulty count · BPM) and a play button to preview the
 *             song. Triggered when `bpm`/`diffCount`/`onTogglePreview` is given.
 *   • Simple — local files (.flow / .synth) that only have name/date/size: shows
 *             a single `meta` line, no pills.
 *
 * `onDelete` is optional; when present a trash button reveals on hover with a
 * confirm step. `cleanName` lets the caller strip a file extension.
 */

import React, { useState } from 'react';
import { color, font } from './theme';
import { IcTrash, IcTriRight, IcFolder } from './icons';
import CoverThumb from './CoverThumb';

export default function MapListRow({
  name, artist, mapper, meta, bpm, diffCount, cover,
  active = false, previewing = false, isPublic = false,
  onPick, onTogglePreview, onOpenFolder, onDelete, onToggleVisibility, cleanName,
}: {
  name: string;
  artist?: string;
  mapper?: string;
  meta?: string;
  bpm?: number;
  diffCount?: number;
  cover?: string | null;
  active?: boolean;
  previewing?: boolean;
  /** When true, shows a "Public" badge on the row. */
  isPublic?: boolean;
  onPick: () => void;
  onTogglePreview?: () => void;
  /** When set, shows a folder button that reveals the map's file on disk. */
  onOpenFolder?: () => void;
  onDelete?: () => void;
  /** When set (owner only), shows a publish/unpublish toggle button. */
  onToggleVisibility?: () => void;
  cleanName?: (n: string) => string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [hovered, setHovered] = useState(false);
  const label = cleanName ? cleanName(name) : name;
  const showPills = bpm != null || diffCount != null || !!onTogglePreview || !!onOpenFolder || isPublic || !!onToggleVisibility;

  return (
    <li
      style={{ ...itemStyle, ...(active ? itemActiveStyle : null) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirming(false); }}
    >
      <button onClick={onPick} style={itemMainStyle} title="Click to load">
        <CoverThumb src={cover} name={label} size={56} />
        <span style={itemTextStyle}>
          <span style={itemNameStyle}>{label}</span>
          {artist && <span style={itemArtistStyle}>{artist}</span>}
          {mapper && <span style={itemMapperStyle}>mapped by {mapper}</span>}
          {!artist && !mapper && meta && <span style={itemArtistStyle}>{meta}</span>}
        </span>
      </button>

      {/* Right-side pills: difficulty count · BPM · preview-play button. */}
      {showPills && (
        <div style={pillsRowStyle}>
          {isPublic && <span style={publicPillStyle}>Public</span>}
          {onToggleVisibility && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
              style={visPillStyle}
              title={isPublic ? 'Make private' : 'Make public'}
            >
              {isPublic ? 'Unpublish' : 'Publish'}
            </button>
          )}
          {diffCount != null && (
            <span style={pillStyle}>{diffCount} diff{diffCount === 1 ? '' : 's'}</span>
          )}
          {bpm != null && <span style={pillStyle}>{bpm} BPM</span>}
          {onOpenFolder && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenFolder(); }}
              style={folderPillStyle}
              title="Open the folder where this file is saved"
            >
              <IcFolder size={13} />
            </button>
          )}
          {onTogglePreview && (
            <button
              onClick={(e) => { e.stopPropagation(); onTogglePreview(); }}
              style={{ ...playPillStyle, ...(previewing ? playPillActiveStyle : null) }}
              title={previewing ? 'Stop preview' : 'Listen to the song'}
            >
              {previewing ? '❚❚' : <IcTriRight size={11} />}
            </button>
          )}
        </div>
      )}

      {/* Delete control lives in an always-mounted slot whose width animates
          from 0 → open, so the pills slide left smoothly on hover (no jump). */}
      {onDelete && (
        <div style={{ ...delSlotStyle, width: confirming ? 100 : (hovered ? 42 : 0), opacity: hovered || confirming ? 1 : 0 }}>
          {confirming ? (
            <div style={confirmRowStyle}>
              <button onClick={onDelete} style={confirmDelBtnStyle} title="Confirm delete">Delete</button>
              <button onClick={() => setConfirming(false)} style={confirmCancelBtnStyle}>×</button>
            </div>
          ) : (
            <button onClick={() => setConfirming(true)} style={deleteBtnStyle} title="Remove from list"><IcTrash size={14} /></button>
          )}
        </div>
      )}
    </li>
  );
}

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'stretch', gap: 6,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)', borderRadius: 10,
  background: 'rgba(15, 15, 30, 0.3)', overflow: 'hidden',
};
const itemActiveStyle: React.CSSProperties = {
  borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.45)',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.15), oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.08))',
};
const itemMainStyle: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
  background: 'transparent', border: 'none', cursor: 'pointer',
  padding: 10, fontFamily: 'inherit', minWidth: 0,
};
const itemTextStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 };
const itemNameStyle: React.CSSProperties = { color: color.ink, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const itemArtistStyle: React.CSSProperties = { color: color.inkDim, fontSize: 11, fontFamily: font.sans, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const itemMapperStyle: React.CSSProperties = { color: color.inkFaint, fontSize: 10, fontFamily: font.sans, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const pillsRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', flexShrink: 0 };
const pillStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: color.accentSoft, whiteSpace: 'nowrap', fontFamily: font.sans,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  borderRadius: 999, padding: '3px 9px',
};
const publicPillStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: 'oklch(0.85 0.14 155)', whiteSpace: 'nowrap', fontFamily: font.sans,
  background: 'oklch(0.55 0.14 155 / 0.16)',
  border: '1px solid oklch(0.70 0.16 155 / 0.4)',
  borderRadius: 999, padding: '3px 9px',
};
const visPillStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: color.inkDim, whiteSpace: 'nowrap', fontFamily: font.sans,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  borderRadius: 999, padding: '3px 9px', cursor: 'pointer',
};
const playPillStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0,
  borderRadius: '50%', cursor: 'pointer', color: 'var(--accent)', fontSize: 10, fontFamily: font.sans,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  transition: 'all 0.15s ease',
};
const playPillActiveStyle: React.CSSProperties = {
  background: 'var(--accent)', color: 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))', borderColor: 'var(--accent)',
};
const folderPillStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0,
  borderRadius: '50%', cursor: 'pointer', color: color.inkDim, fontSize: 10, fontFamily: font.sans,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  transition: 'all 0.15s ease',
};
// Slot wrapping the delete control — clips its content so the button can slide
// in/out as the width animates between 0 and its open size.
const delSlotStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'stretch', overflow: 'hidden', flexShrink: 0,
  transition: 'width 0.24s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease',
};
const deleteBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer', fontSize: 13, padding: '0 12px',
  flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center',
};
const confirmRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap' };
const confirmDelBtnStyle: React.CSSProperties = {
  background: 'rgba(239, 68, 68, 0.15)', border: 'none', color: '#F87171', cursor: 'pointer',
  fontSize: 11, fontWeight: 600, padding: '0 10px', height: '100%', whiteSpace: 'nowrap',
};
const confirmCancelBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer', fontSize: 14, padding: '0 8px',
};
